"use client";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";
import { AccountService } from "@/Services/AccountService";
import type { WorkspaceProfile } from "@/Models/WorkspaceProfile";
import { ProfileStatus } from "@/Components/Account/ProfileStatus";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { useWorkspace } from "../workspace";
import { Header } from "./header";
import { Footer } from "./footer";
import { Sidebar } from "./sidebar";
import { WorkspacePanels } from "./workspace-panels";
import { WorkspaceToast } from "./workspace-toast";
import type { WorkspacePanel } from "./types";

type ShellProps = {
  children: ReactNode;
  user: WorkspaceProfile;
};

export const Shell: FC<ShellProps> = ({ children, user }) => {
  const [queryClient] = useState(() => new QueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <WorkspaceShell user={user}>{children}</WorkspaceShell>
    </QueryClientProvider>
  );
};

const WorkspaceShell: FC<ShellProps> = ({ children, user }) => {
  const { locale, t } = useWorkspace();
  const profileQuery = useQuery({
    queryKey: ["account-profile", locale],
    queryFn: () => AccountService.getProfile(locale),
    enabled: !!locale,
    select: (response) => response.data,
    staleTime: 60_000,
    retry: false,
  });
  const profile = profileQuery.data ?? user;
  const profileStatus = profileQuery.isPending
    ? "pending"
    : profileQuery.isError
      ? "error"
      : profileQuery.data
        ? "ready"
        : "empty";
  const [mobile, setMobile] = useState(false);
  const [panel, setPanel] = useState<WorkspacePanel | null>(null);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  useEffect(() => {
    const newClient = () => setPanel("new");
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobile(false);
    };
    window.addEventListener("baken:new-client", newClient);
    document.addEventListener("keydown", escape);
    return () => {
      window.removeEventListener("baken:new-client", newClient);
      document.removeEventListener("keydown", escape);
    };
  }, []);
  return (
    <>
      <a
        href="#main-content"
        className="skip-link fixed -top-20 left-[260px] z-200 bg-teal text-white py-3 px-4.5 rounded-lg [&:focus]:top-2.5"
      >
        {t("skip")}
      </a>
      <Sidebar
        user={profile}
        profileStatus={<ProfileStatus status={profileStatus} />}
        open={mobile}
        onClose={() => setMobile(false)}
      />
      <div className="app-wrap ml-[242px] min-h-screen flex flex-col min-w-0 max-[1200px]:ml-[220px] max-[760px]:ml-0">
        <Header
          user={profile}
          onOpenMenu={() => setMobile(true)}
          onOpenPanel={setPanel}
        />
        <main
          className="w-full max-w-[1490px] my-0 mx-auto pt-7.75 px-7.5 pb-8.75 flex-1 outline-none min-[1600px]:p-9.5 max-[1200px]:py-6.25 max-[1200px]:px-5.5 max-[760px]:py-6 max-[760px]:px-4.25"
          id="main-content"
          tabIndex={-1}
        >
          {children}
        </main>
        <Footer />
      </div>
      <WorkspaceToast />
      {panel && (
        <WorkspacePanels panel={panel} onClose={() => setPanel(null)} />
      )}
    </>
  );
};
